import React, { useState } from 'react';
import Check from 'lucide-react/dist/esm/icons/check.js';
import DrawLifecycleMessage from '../DrawLifecycleMessage';
import { useTranslations } from '../../contexts/TranslationContext';
import '../../styles/storybook.css';

/* ============================================================
 *  Storybook Hero — Pass 2 (painterly steampunk / cosmic)
 *
 *  Visual goals (vs. Pass 1):
 *   - Glass crystal orb with floating cards inside, coins outside
 *   - Clockwork countdown machine: gears + telescope + brass frame
 *   - Stone parapet footer with arched candle alcoves
 *   - Wooden-dowel scroll ends with cartoon mitten hands
 *   - Plaque with rivets, ribbon side-folds, embossed parchment
 *   - Inline SVG ticket + trophy (no emoji)
 *   - Painterly partner ribbons (gold inner border, fabric folds)
 *   - Nebula swirl + drifting glowing aces + vignette
 *   - Layered gold wordmark with stroke + letterpress
 * ============================================================ */

/* ────────────────────────────────────────────────────────────
 * 1. SVG primitives
 * ──────────────────────────────────────────────────────────── */

const Gear = ({ size = 36, className = '', reverse = false, teeth = 12, speed = 24 }) => {
  const id = React.useId();
  return (
    <svg
      width={size} height={size} viewBox="0 0 64 64"
      className={`${className} ${reverse ? 'sb-gear-rev' : 'sb-gear'}`}
      style={{ animationDuration: `${speed}s` }}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`gear-${id}`} cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#ffe9b3" />
          <stop offset="55%" stopColor="#d99c2e" />
          <stop offset="100%" stopColor="#5a3a0c" />
        </radialGradient>
      </defs>
      <g fill={`url(#gear-${id})`} stroke="#2a1808" strokeWidth="1.2">
        {Array.from({ length: teeth }).map((_, i) => {
          const a = (i * (360 / teeth)) * Math.PI / 180;
          const x = 32 + Math.cos(a) * 28;
          const y = 32 + Math.sin(a) * 28;
          return <rect key={i} x={x - 3.5} y={y - 3.5} width="7" height="7" transform={`rotate(${i * (360 / teeth)} ${x} ${y})`} />;
        })}
        <circle cx="32" cy="32" r="22" />
        <circle cx="32" cy="32" r="14" fill="#3a2407" stroke="#1a0e02" strokeWidth="1" />
        <circle cx="32" cy="32" r="9" fill="#1a0e02" stroke="none" />
        {/* Spoke holes */}
        {Array.from({ length: 5 }).map((_, i) => {
          const a = (i * 72) * Math.PI / 180;
          return <circle key={i} cx={32 + Math.cos(a) * 18} cy={32 + Math.sin(a) * 18} r="1.6" fill="#1a0e02" stroke="none" />;
        })}
      </g>
    </svg>
  );
};

const Telescope = ({ className = '' }) => (
  <svg viewBox="0 0 140 90" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="sb-tel-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#ffe9b3" />
        <stop offset="50%" stopColor="#d99c2e" />
        <stop offset="100%" stopColor="#5a3a0c" />
      </linearGradient>
      <linearGradient id="sb-tel-rim" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff3b0" />
        <stop offset="100%" stopColor="#7a5111" />
      </linearGradient>
    </defs>
    <g stroke="#2a1808" strokeWidth="1.5">
      {/* Tripod base */}
      <rect x="6" y="68" width="20" height="6" rx="2" fill="url(#sb-tel-grad)" />
      <rect x="13" y="50" width="6" height="22" fill="url(#sb-tel-grad)" />
      {/* Body sections (3 telescoping segments) */}
      <rect x="8" y="44" width="50" height="14" rx="3" transform="rotate(-18 33 51)" fill="url(#sb-tel-grad)" />
      <rect x="48" y="36" width="42" height="18" rx="3" transform="rotate(-18 69 45)" fill="url(#sb-tel-grad)" />
      <rect x="82" y="28" width="32" height="22" rx="3" transform="rotate(-18 98 39)" fill="url(#sb-tel-grad)" />
      {/* Eyepiece end ring */}
      <ellipse cx="118" cy="30" rx="6" ry="11" transform="rotate(-18 118 30)" fill="url(#sb-tel-rim)" />
      {/* Lens glow */}
      <ellipse cx="118" cy="30" rx="3" ry="7" transform="rotate(-18 118 30)" fill="#a8d4ff" stroke="#2a1808" strokeWidth="0.8" />
      {/* Rivets on body */}
      <circle cx="22" cy="52" r="1.2" fill="#1a0e02" stroke="none" />
      <circle cx="58" cy="44" r="1.2" fill="#1a0e02" stroke="none" />
      <circle cx="92" cy="34" r="1.2" fill="#1a0e02" stroke="none" />
    </g>
  </svg>
);

const Coin = ({ className = '', style }) => {
  const id = React.useId();
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
      <defs>
        <radialGradient id={`coin-${id}`} cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#fff3b0" />
          <stop offset="50%" stopColor="#f5c84b" />
          <stop offset="100%" stopColor="#7a5111" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill={`url(#coin-${id})`} stroke="#2a1808" strokeWidth="1.2" />
      <circle cx="12" cy="12" r="7.5" fill="none" stroke="#7a5111" strokeWidth="0.6" opacity="0.6" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="900" fill="#3a2407" fontFamily="Georgia, serif">$</text>
    </svg>
  );
};

const Planet = ({ color = '#3aa9d6', ring = false, face = true, className = '', style }) => {
  const safe = color.replace('#', '');
  const id = React.useId();
  return (
    <svg viewBox="0 0 100 100" className={className} style={style} aria-hidden="true">
      <defs>
        <radialGradient id={`pl-${safe}-${id}`} cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.6" />
          <stop offset="35%" stopColor={color} />
          <stop offset="100%" stopColor="#1a0e3a" />
        </radialGradient>
        <filter id={`pl-shadow-${id}`}>
          <feGaussianBlur stdDeviation="2" />
        </filter>
      </defs>
      {/* drop shadow under planet */}
      <ellipse cx="52" cy="84" rx="28" ry="3" fill="rgba(0,0,0,0.4)" filter={`url(#pl-shadow-${id})`} />
      {ring && (
        <ellipse cx="50" cy="55" rx="48" ry="10" fill="none" stroke="#e8b14a" strokeWidth="3" opacity="0.85" />
      )}
      <circle cx="50" cy="50" r="32" fill={`url(#pl-${safe}-${id})`} stroke="#0b0828" strokeWidth="1.5" />
      {ring && (
        <ellipse cx="50" cy="55" rx="48" ry="10" fill="none" stroke="#e8b14a" strokeWidth="3" opacity="0.6" clipPath="inset(0 0 50% 0)" />
      )}
      {face && (
        <g fill="#0b0828">
          <circle cx="40" cy="46" r="2.4" />
          <circle cx="58" cy="46" r="2.4" />
          <path d="M40 56 Q49 64 58 56" stroke="#0b0828" strokeWidth="2" fill="none" strokeLinecap="round" />
          <ellipse cx="36" cy="54" rx="3" ry="1.5" fill="#ff8da1" opacity="0.6" />
          <ellipse cx="62" cy="54" rx="3" ry="1.5" fill="#ff8da1" opacity="0.6" />
        </g>
      )}
    </svg>
  );
};

const Star = ({ className = '', style, glint = false }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
    <path
      d="M12 2 L14 9 L21 10 L15.5 14.5 L17.5 22 L12 17.5 L6.5 22 L8.5 14.5 L3 10 L10 9 Z"
      fill="#ffd76b" stroke="#a36b10" strokeWidth="0.8" strokeLinejoin="round"
    />
    {glint && <circle cx="12" cy="11" r="2" fill="#fff8d8" opacity="0.8" />}
  </svg>
);

const PlayingCardSvg = ({ value = 'A', suit = '♠', className = '', style, glow = false }) => {
  const isRed = suit === '♥' || suit === '♦';
  const fg = isRed ? '#c2253a' : '#1a1a1a';
  const id = React.useId();
  return (
    <svg viewBox="0 0 60 86" className={className} style={style} aria-hidden="true">
      <defs>
        {glow && (
          <filter id={`card-glow-${id}`} x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="2.5" result="b" />
            <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        )}
      </defs>
      <g filter={glow ? `url(#card-glow-${id})` : undefined}>
        <rect x="2" y="2" width="56" height="82" rx="6" fill="#fffaf0" stroke="#7a6240" strokeWidth="1.5" />
        <text x="8" y="16" fontSize="12" fontWeight="900" fill={fg} fontFamily="Georgia, serif">{value}</text>
        <text x="8" y="28" fontSize="12" fill={fg} fontFamily="Georgia, serif">{suit}</text>
        <text x="30" y="52" textAnchor="middle" fontSize="28" fill={fg} fontFamily="Georgia, serif">{suit}</text>
        <text x="52" y="78" fontSize="12" fontWeight="900" fill={fg} fontFamily="Georgia, serif" transform="rotate(180 52 74)">{value}</text>
        <text x="52" y="66" fontSize="12" fill={fg} fontFamily="Georgia, serif" transform="rotate(180 52 62)">{suit}</text>
      </g>
    </svg>
  );
};

/* Inline ticket-stub icon (left side of CTA button) */
const TicketIconSvg = ({ className = '' }) => (
  <svg viewBox="0 0 32 20" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="tick-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff3b0" />
        <stop offset="100%" stopColor="#d99c2e" />
      </linearGradient>
    </defs>
    <path
      d="M2 4 Q2 2 4 2 H12 Q12 4 14 4 Q16 4 16 2 H28 Q30 2 30 4 V16 Q30 18 28 18 H16 Q16 16 14 16 Q12 16 12 18 H4 Q2 18 2 16 Z"
      fill="url(#tick-grad)" stroke="#5a3a0c" strokeWidth="1.2" strokeLinejoin="round"
    />
    <line x1="14" y1="6" x2="14" y2="14" stroke="#5a3a0c" strokeWidth="0.8" strokeDasharray="1 1.5" />
  </svg>
);

/* Inline trophy icon (right side of CTA) */
const TrophyIconSvg = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="tro-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fff3b0" />
        <stop offset="100%" stopColor="#c98a14" />
      </linearGradient>
    </defs>
    <g fill="url(#tro-grad)" stroke="#5a3a0c" strokeWidth="1" strokeLinejoin="round">
      <path d="M7 3 H17 V7 Q17 11 12 12 Q7 11 7 7 Z" />
      <path d="M5 4 Q3 4 3 6 Q3 9 7 9" fill="none" />
      <path d="M19 4 Q21 4 21 6 Q21 9 17 9" fill="none" />
      <rect x="10" y="12" width="4" height="4" />
      <rect x="7" y="16" width="10" height="2.5" rx="0.5" />
    </g>
  </svg>
);

/* Cartoon mitten gripping a wooden dowel — used on scroll ends */
const MittenHand = ({ flip = false, className = '' }) => (
  <svg viewBox="0 0 40 50" className={className} style={{ transform: flip ? 'scaleX(-1)' : undefined }} aria-hidden="true">
    <defs>
      <linearGradient id="mitten-grad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#9aa3b0" />
        <stop offset="100%" stopColor="#5a6270" />
      </linearGradient>
    </defs>
    <g stroke="#1a1f28" strokeWidth="1.3" strokeLinejoin="round">
      {/* Cuff */}
      <rect x="4" y="2" width="20" height="8" rx="2" fill="#3a4250" />
      {/* Palm */}
      <path d="M4 10 Q2 12 4 30 Q6 40 18 40 Q34 40 34 26 Q34 22 30 22 L24 22 Q22 22 22 20 L22 10 Z" fill="url(#mitten-grad)" />
      {/* Thumb */}
      <path d="M30 22 Q38 20 36 14 Q34 10 28 14 Z" fill="url(#mitten-grad)" />
      {/* Knuckle line */}
      <path d="M10 28 Q18 32 28 28" stroke="#1a1f28" strokeWidth="0.8" fill="none" opacity="0.5" />
    </g>
  </svg>
);

/* Wooden dowel (vertical rod with end caps) — used on scroll ends */
const WoodenDowel = ({ className = '' }) => (
  <svg viewBox="0 0 24 90" className={className} aria-hidden="true" preserveAspectRatio="none">
    <defs>
      <linearGradient id="dowel-grad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#5a3a0c" />
        <stop offset="40%" stopColor="#a8722a" />
        <stop offset="60%" stopColor="#a8722a" />
        <stop offset="100%" stopColor="#5a3a0c" />
      </linearGradient>
      <linearGradient id="dowel-cap" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#c69552" />
        <stop offset="100%" stopColor="#5a3a0c" />
      </linearGradient>
    </defs>
    <rect x="6" y="10" width="12" height="70" fill="url(#dowel-grad)" stroke="#3a2407" strokeWidth="1" />
    <ellipse cx="12" cy="10" rx="9" ry="5" fill="url(#dowel-cap)" stroke="#3a2407" strokeWidth="1" />
    <ellipse cx="12" cy="80" rx="9" ry="5" fill="url(#dowel-cap)" stroke="#3a2407" strokeWidth="1" />
    {/* wood grain */}
    <path d="M10 18 Q13 40 10 70" stroke="#3a2407" strokeWidth="0.5" fill="none" opacity="0.4" />
    <path d="M14 16 Q11 42 14 72" stroke="#3a2407" strokeWidth="0.5" fill="none" opacity="0.4" />
  </svg>
);

/* Candle for stone alcove */
const Candle = () => (
  <svg viewBox="0 0 24 40" className="w-5 h-9" aria-hidden="true">
    <ellipse cx="12" cy="36" rx="6" ry="2" fill="rgba(0,0,0,0.5)" />
    <rect x="9" y="14" width="6" height="22" fill="#f5e6b8" stroke="#7a6240" strokeWidth="1" />
    <ellipse cx="12" cy="14" rx="3" ry="1" fill="#d9c388" />
    {/* drips */}
    <path d="M9 20 Q8 26 10 28" stroke="#7a6240" strokeWidth="0.5" fill="#f5e6b8" />
    {/* flame */}
    <ellipse cx="12" cy="11" rx="6" ry="9" fill="#ffaa3b" opacity="0.25">
      <animate attributeName="opacity" values="0.18;0.32;0.18" dur="1.8s" repeatCount="indefinite" />
    </ellipse>
    <path d="M12 4 Q14 8 12 13 Q10 8 12 4 Z" fill="#ffd76b">
      <animate attributeName="opacity" values="0.85;1;0.85" dur="1.6s" repeatCount="indefinite" />
    </path>
    <circle cx="12" cy="12" r="1.3" fill="#fffae6" />
  </svg>
);

/* ────────────────────────────────────────────────────────────
 * 2. Background — nebula + stars + drifting cards + planets
 * ──────────────────────────────────────────────────────────── */

const StarfieldBackdrop = ({ targetCard = { rank: 'A', suit: '\u2660' }, showCards = true }) => (
  <div className="absolute inset-0 storybook-cosmic overflow-hidden pointer-events-none">
    {/* Nebula swirls — large soft radial blobs */}
    <div className="sb-nebula sb-nebula-1" />
    <div className="sb-nebula sb-nebula-2" />
    <div className="sb-nebula sb-nebula-3" />

    {/* Vignette */}
    <div className="sb-vignette" />

    {/* Big ringed Saturn-style planet, bottom-left */}
    <Planet
      color="#3aa9d6" ring
      className="absolute -bottom-6 -left-6 w-44 h-44 sm:w-56 sm:h-56 storybook-drift"
      style={{ '--rot': '-6deg' }}
    />
    {/* Medium green smiling planet, top center */}
    <Planet
      color="#5cc85e"
      className="absolute top-[10%] left-[38%] w-20 h-20 sm:w-24 sm:h-24 storybook-drift hidden sm:block"
      style={{ '--rot': '4deg', animationDelay: '1.2s' }}
    />
    {/* Mid-right teal smiling planet */}
    <Planet
      color="#3ec0c2"
      className="absolute top-[42%] right-[3%] w-24 h-24 sm:w-28 sm:h-28 storybook-drift hidden md:block"
      style={{ '--rot': '-4deg', animationDelay: '2s' }}
    />
    {/* Small purple swirl planet (no face) */}
    <Planet
      color="#7a6bd0" face={false}
      className="absolute top-[34%] left-[22%] w-14 h-14 storybook-drift hidden lg:block"
      style={{ '--rot': '8deg', animationDelay: '0.6s' }}
    />

    {/* Drifting glowing jackpot cards in the central empty space */}
    {showCards && (
      <>
        <PlayingCardSvg value={targetCard.rank} suit={targetCard.suit} glow
          className="absolute top-[14%] left-[3%] w-12 sm:w-14 storybook-drift opacity-90"
          style={{ '--rot': '-16deg' }}
        />
        <PlayingCardSvg value={targetCard.rank} suit={targetCard.suit} glow
          className="absolute top-[26%] left-[28%] w-10 sm:w-12 storybook-drift-slow opacity-75 hidden sm:block"
          style={{ '--rot': '12deg', animationDelay: '1.4s' }}
        />
        <PlayingCardSvg value={targetCard.rank} suit={targetCard.suit} glow
          className="absolute top-[58%] left-[34%] w-10 sm:w-12 storybook-drift opacity-70 hidden md:block"
          style={{ '--rot': '-8deg', animationDelay: '2.1s' }}
        />
        <PlayingCardSvg value={targetCard.rank} suit={targetCard.suit} glow
          className="absolute top-[68%] right-[42%] w-9 sm:w-11 storybook-drift-slow opacity-65 hidden lg:block"
          style={{ '--rot': '6deg', animationDelay: '3s' }}
        />
      </>
    )}

    {/* Stars — small + medium with cross-glints + twinkle */}
    <Star className="absolute top-[6%]  left-[12%] w-4 h-4 sb-twinkle" />
    <Star className="absolute top-[28%] left-[58%] w-3 h-3" />
    <Star className="absolute top-[50%] left-[6%]  w-5 h-5" glint />
    <Star className="absolute top-[8%]  right-[12%] w-3 h-3 sb-twinkle" style={{ animationDelay: '1s' }} />
    <Star className="absolute top-[80%] left-[44%] w-3 h-3" />
    <Star className="absolute top-[88%] right-[6%]  w-4 h-4" glint />
    <Star className="absolute top-[36%] right-[28%] w-3 h-3 sb-twinkle" style={{ animationDelay: '2s' }} />
    <Star className="absolute top-[64%] right-[18%] w-3 h-3 hidden md:block" />
    <Star className="absolute top-[18%] left-[68%] w-2.5 h-2.5 sb-twinkle" style={{ animationDelay: '0.4s' }} />
    <Star className="absolute top-[72%] left-[8%]  w-2.5 h-2.5" />
    <Star className="absolute top-[44%] left-[48%] w-2 h-2" />
  </div>
);

/* ────────────────────────────────────────────────────────────
 * 3. Ornate plaque with rivets, ribbon side-folds, embossed inset
 * ──────────────────────────────────────────────────────────── */

const PlaqueRivets = () => (
  <>
    {/* 8 rivets around inside border (tl,tc,tr,r,br,bc,bl,l) */}
    <span className="sb-rivet absolute top-2 left-2" />
    <span className="sb-rivet absolute top-2 left-1/2 -translate-x-1/2" />
    <span className="sb-rivet absolute top-2 right-2" />
    <span className="sb-rivet absolute top-1/2 -translate-y-1/2 right-2" />
    <span className="sb-rivet absolute bottom-2 right-2" />
    <span className="sb-rivet absolute bottom-2 left-1/2 -translate-x-1/2" />
    <span className="sb-rivet absolute bottom-2 left-2" />
    <span className="sb-rivet absolute top-1/2 -translate-y-1/2 left-2" />
  </>
);

const Wordmark = ({ siteLogoUrl, siteName }) => {
  if (siteLogoUrl) {
    return (
      <div className="sb-wordmark text-center select-none flex items-center justify-center">
        <img
          src={siteLogoUrl}
          alt={siteName || 'Site logo'}
          className="max-h-[88px] sm:max-h-[110px] md:max-h-[130px] max-w-[80%] object-contain mx-auto"
          style={{ filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.55)) drop-shadow(0 0 18px rgba(255,210,120,0.18))' }}
        />
      </div>
    );
  }
  return (
    <div className="sb-wordmark text-center select-none">
      <div className="sb-wordmark-line">CHASE</div>
      <div className="sb-wordmark-line">THE ACE</div>
    </div>
  );
};

const OrnatePlaque = ({ weekNumber, liveLabel, charityLogoUrl, charityName, siteLogoUrl, siteName, weekLabel = 'Week', logoBackgroundColor = '#ffffff' }) => (
  <div className="relative inline-block">
    <div className="relative mt-2">
      {/* Side ribbon-banner folds */}
      <span className="sb-ribbon-fold sb-ribbon-fold-left" aria-hidden="true" />
      <span className="sb-ribbon-fold sb-ribbon-fold-right" aria-hidden="true" />

      <div className="relative sb-gold sb-plaque-emboss rounded-2xl px-6 py-4 border-4 border-amber-950/80 min-w-[240px]">
        {/* Gear corners */}
        <Gear size={42} className="absolute -top-5 -left-6" />
        <Gear size={42} className="absolute -top-5 -right-6" reverse />
        <Gear size={42} className="absolute -bottom-5 -left-6" reverse />
        <Gear size={42} className="absolute -bottom-5 -right-6" />

        <PlaqueRivets />

        {/* Charity logo — admin-configurable background applies whether or not a
            logo image exists. The parchment texture is only used as the default
            (white) fallback when there's no logo and no explicit color override. */}
        {(() => {
          const hasExplicitBg = logoBackgroundColor && logoBackgroundColor !== '#ffffff';
          const useParchment = !charityLogoUrl && !hasExplicitBg;
          return (
        <div
          className={`mx-auto w-28 h-24 sm:w-32 sm:h-28 rounded-md border-2 border-amber-900/70 flex items-center justify-center overflow-hidden relative${charityLogoUrl ? ' p-2' : ''}${useParchment ? ' sb-parchment sb-parchment-noise' : ''}`}
          style={!useParchment ? { backgroundColor: logoBackgroundColor || '#ffffff' } : undefined}
        >
          {charityLogoUrl ? (
            <img src={charityLogoUrl} alt={charityName || 'Charity'} className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="relative z-10 text-4xl font-black text-rose-700 sb-painted" style={{ fontFamily: 'Georgia, serif' }}>
              {(charityName || 'Y').slice(0, 1)}
            </span>
          )}
        </div>
          );
        })()}
      </div>
    </div>

    {/* Week / live pill */}
    {weekNumber != null && (
      <div className="mt-4 flex justify-center">
        <div className="sb-live-pill inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-white text-xs font-black tracking-wider uppercase">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-200 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white shadow-[0_0_6px_#fff]" />
          </span>
          {weekLabel} {weekNumber} — {liveLabel}
        </div>
      </div>
    )}
  </div>
);

/* ────────────────────────────────────────────────────────────
 * 4. Big "CHASE THE ACE" wordmark below plaque
 * ──────────────────────────────────────────────────────────── */

const StorybookTitle = ({ children }) => (
  <h1 className="sb-bigtitle text-center select-none">
    {children || (<>CHASE<span className="block">THE ACE</span></>)}
  </h1>
);

/* ────────────────────────────────────────────────────────────
 * 5. Scroll banner — wooden dowels with mitten hands gripping
 * ──────────────────────────────────────────────────────────── */

const ScrollBanner = ({ children }) => (
  <div className="relative inline-flex items-center max-w-2xl">
    {/* Left dowel + hand */}
    <div className="relative w-8 h-20 -mr-1 z-20 flex items-center justify-center">
      <WoodenDowel className="absolute inset-0 w-full h-full" />
      <MittenHand className="absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-12 z-30" />
    </div>
    <div className="sb-parchment sb-parchment-noise relative px-7 py-4 border-y-2 border-amber-900/50 text-amber-950 font-semibold text-sm sm:text-base leading-snug text-center sb-scroll-sag">
      {children}
    </div>
    {/* Right dowel + hand */}
    <div className="relative w-8 h-20 -ml-1 z-20 flex items-center justify-center">
      <WoodenDowel className="absolute inset-0 w-full h-full" />
      <MittenHand flip className="absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-12 z-30" />
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────
 * 6. Ticket-shaped CTA button (SVG ticket + trophy, no emoji)
 * ──────────────────────────────────────────────────────────── */

const TicketButton = ({ onClick, children, size = 'lg', disabled = false }) => {
  const pad = size === 'lg' ? 'px-10 py-3.5 text-base sm:text-lg' : 'px-6 py-2.5 text-sm';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`sb-ticket sb-parchment ${pad} text-amber-950 font-black uppercase tracking-wider inline-flex items-center justify-center gap-3 border-2 border-amber-900/60 relative transition-transform ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.03]'}`}
      style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
    >
      <TicketIconSvg className="w-7 h-5" />
      <span className="whitespace-nowrap">{children}</span>
      <TrophyIconSvg className="w-5 h-5" />
    </button>
  );
};

/* ────────────────────────────────────────────────────────────
 * 7. Previous draws
 * ──────────────────────────────────────────────────────────── */

const PreviousDraws = ({ label, eliminatedCards }) => {
  const last2 = (eliminatedCards || []).slice(-2);
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-amber-200/90 text-[11px] font-black uppercase tracking-[0.2em]">{label}</span>
      {last2.length > 0 && (
        <div className="flex gap-2">
          {last2.map((card, i) => {
            const value = String(card).slice(0, -1);
            const suit = String(card).slice(-1);
            return <PlayingCardSvg key={i} value={value} suit={suit} className="w-10 h-14 drop-shadow-lg" />;
          })}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
 * 8. Crystal ball — proper glass orb (SVG-based)
 * ──────────────────────────────────────────────────────────── */

const CrystalBall = ({ amount, potBuildingText = 'Pot Building', ribbonText }) => {
  const id = React.useId();
  return (
    <div className="relative flex flex-col items-center">
      <div className="relative w-56 h-56 sm:w-64 sm:h-64">
        {/* Outer glow */}
        <div className="absolute inset-[-18px] rounded-full sb-crystal-glow pointer-events-none" />

        {/* SVG orb */}
        <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full drop-shadow-[0_18px_28px_rgba(0,0,0,0.55)]" aria-hidden="true">
          <defs>
            <radialGradient id={`orb-fill-${id}`} cx="35%" cy="32%" r="75%">
              <stop offset="0%" stopColor="#dfeeff" stopOpacity="0.95" />
              <stop offset="22%" stopColor="#9ec3f3" stopOpacity="0.85" />
              <stop offset="55%" stopColor="#3a6bbd" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#0c1840" stopOpacity="0.95" />
            </radialGradient>
            <radialGradient id={`orb-rim-${id}`} cx="50%" cy="50%" r="50%">
              <stop offset="86%" stopColor="rgba(255,255,255,0)" />
              <stop offset="93%" stopColor="rgba(180,210,255,0.55)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.9)" />
            </radialGradient>
            <filter id={`orb-shadow-${id}`}>
              <feGaussianBlur stdDeviation="2" />
            </filter>
          </defs>
          <circle cx="100" cy="100" r="92" fill={`url(#orb-fill-${id})`} />
          {/* Rim light */}
          <circle cx="100" cy="100" r="92" fill={`url(#orb-rim-${id})`} />
          {/* Top-left highlight crescent */}
          <ellipse cx="72" cy="64" rx="32" ry="18" fill="rgba(255,255,255,0.55)" transform="rotate(-30 72 64)" />
          <ellipse cx="68" cy="58" rx="14" ry="6" fill="rgba(255,255,255,0.85)" transform="rotate(-30 68 58)" />
          {/* Bottom inner shadow */}
          <ellipse cx="110" cy="160" rx="60" ry="22" fill="rgba(0,0,0,0.35)" filter={`url(#orb-shadow-${id})`} />
          {/* Base shadow on ground */}
        </svg>
        <svg viewBox="0 0 200 30" className="absolute -bottom-4 left-0 w-full h-6" aria-hidden="true">
          <ellipse cx="100" cy="15" rx="70" ry="8" fill="rgba(0,0,0,0.45)" />
        </svg>

        {/* Floating cards INSIDE the orb (clipped via overflow-hidden round) */}
        <div className="absolute inset-4 rounded-full overflow-hidden pointer-events-none">
          <PlayingCardSvg value="A" suit="♠" className="absolute top-[14%] left-[16%] w-9 storybook-drift opacity-90" style={{ '--rot': '-22deg', animationDelay: '0.4s' }} />
          <PlayingCardSvg value="A" suit="♠" className="absolute bottom-[20%] left-[12%] w-7 storybook-drift-slow opacity-75" style={{ '--rot': '14deg', animationDelay: '2.2s' }} />
          <PlayingCardSvg value="A" suit="♠" className="absolute top-[28%] right-[14%] w-7 storybook-drift opacity-70" style={{ '--rot': '8deg', animationDelay: '1.4s' }} />
          <PlayingCardSvg value="A" suit="♠" className="absolute bottom-[14%] right-[20%] w-6 storybook-drift-slow opacity-65" style={{ '--rot': '-12deg', animationDelay: '3s' }} />
        </div>

        {/* Gold coins OUTSIDE the orb (some clipped behind via z-index) */}
        <Coin className="absolute -top-2 right-6 w-8 h-8 storybook-drift z-10" style={{ '--rot': '-8deg' }} />
        <Coin className="absolute top-1/3 -right-3 w-7 h-7 storybook-drift-slow z-10" style={{ '--rot': '6deg', animationDelay: '1s' }} />
        <Coin className="absolute -bottom-2 right-12 w-7 h-7 storybook-drift z-10" style={{ '--rot': '4deg', animationDelay: '1.6s' }} />
        <Coin className="absolute top-6 -left-3 w-6 h-6 storybook-drift-slow z-0 opacity-80" style={{ '--rot': '-12deg', animationDelay: '0.8s' }} />
        <Coin className="absolute bottom-6 -left-2 w-7 h-7 storybook-drift z-10" style={{ '--rot': '10deg', animationDelay: '2.4s' }} />

        {/* Big amount, centered */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
          <div className={`sb-gold-text sb-amount-glow font-black leading-none text-center ${amount > 0 ? 'text-5xl sm:text-6xl' : 'text-2xl sm:text-3xl px-2'}`} style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
            {amount > 0 ? `$${Number(amount).toLocaleString()}` : potBuildingText}
          </div>
        </div>
      </div>

      {/* Parchment ribbon banner under the orb */}
      {ribbonText && (
        <div className="mt-3 relative">
          <div className="sb-parchment-ribbon sb-parchment-noise px-6 py-2 text-amber-950 text-xs sm:text-sm font-semibold text-center min-w-[14rem] border-y-2 border-amber-900/50">
            {ribbonText}
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
 * 9. Partner ribbon stack — fabric folds, gold inner border, medallion
 * ──────────────────────────────────────────────────────────── */

const PARTNER_COLORS = [
  { from: '#c43055', to: '#7d122c', medallion: '#e7c45e' },
  { from: '#c98a2a', to: '#7a5111', medallion: '#fff3b0' },
  { from: '#3da55b', to: '#1d6334', medallion: '#fff3b0' },
  { from: '#d56a2d', to: '#7e3a10', medallion: '#fff3b0' },
  { from: '#5b5dc4', to: '#2b2c75', medallion: '#fff3b0' },
];

const PartnerRibbon = ({ partner, color, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect && onSelect(partner)}
    className={`sb-partner-ribbon group relative flex items-center gap-3 text-white text-left pl-3 pr-3 py-2.5 transition-transform hover:scale-[1.02] ${selected ? 'sb-partner-selected scale-[1.03]' : ''}`}
    style={{ '--from': color.from, '--to': color.to, '--medallion': color.medallion }}
  >
    {/* Medallion */}
    <span className="sb-medallion relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center overflow-hidden">
      {partner.logoUrl ? (
        <img src={partner.logoUrl} alt="" className="w-full h-full object-contain p-0.5" />
      ) : (
        <span className="text-base">{partner.logo || '🏷️'}</span>
      )}
    </span>

    {/* Name */}
    <span className="min-w-0 flex-1">
      <span className="block font-black text-sm leading-tight truncate" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
        {partner.name}
      </span>
      {selected && (
        <span className="block text-[10px] font-bold text-amber-100 uppercase tracking-wider">Selected</span>
      )}
    </span>

    {/* Gold check circle */}
    <span className={`relative flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${selected ? 'sb-check-on' : 'sb-check-off'}`}>
      <Check className="w-4 h-4" strokeWidth={3} />
    </span>
  </button>
);

const PartnerPlaqueStack = ({ partners, selectedId, onSelect, headerLabel = 'Partners' }) => {
  if (!partners || partners.length === 0) return null;
  return (
    <div className="w-full max-w-xs">
      <div className="text-center mb-2">
        <span
          className="sb-gold-text text-2xl font-black tracking-wide"
          style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
        >
          {headerLabel}
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {partners.map((p, i) => (
          <PartnerRibbon
            key={p.id}
            partner={p}
            color={PARTNER_COLORS[i % PARTNER_COLORS.length]}
            selected={selectedId === p.id}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
 * 10. Clockwork countdown — gear stack + telescope + brass mount
 * ──────────────────────────────────────────────────────────── */

const Dial = ({ value, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div className="sb-dial sb-dial-rivets w-12 h-14 sm:w-14 sm:h-16 rounded-md flex items-center justify-center relative">
      <div className="absolute inset-1.5 rounded-sm bg-gradient-to-b from-stone-900 via-amber-950 to-black flex items-center justify-center relative overflow-hidden">
        <span className="font-mono font-black text-xl sm:text-2xl text-amber-200 drop-shadow tabular-nums" style={{ textShadow: '0 0 6px rgba(245,200,75,0.65)' }}>
          {String(value ?? 0).padStart(2, '0')}
        </span>
        {/* Flip seam across middle */}
        <span className="absolute left-0 right-0 top-1/2 h-px bg-black/70" />
        <span className="absolute left-0 right-0 top-1/2 h-[2px] bg-white/5 mt-px" />
      </div>
    </div>
    <span className="text-amber-200 text-[10px] font-black uppercase tracking-widest">{label}</span>
  </div>
);

const ClockworkCountdown = ({ timeLeft, headerLabel = 'Next Draw In', cardsRemaining, labels = {}, cardsRemainingLabel, cardsOddsLabel }) => (
  <div className="relative inline-block">
    {/* Left gear stack — different sizes/speeds, alternating directions */}
    <div className="absolute -left-14 -top-2 hidden sm:block w-20 h-32 pointer-events-none">
      <Gear size={64} className="absolute top-0 left-0" speed={28} />
      <Gear size={42} className="absolute top-12 left-9" reverse speed={18} />
      <Gear size={32} className="absolute top-2 left-12" reverse speed={14} />
      <Gear size={28} className="absolute bottom-0 left-1" speed={20} />
    </div>

    {/* Right telescope angled outward + small gear */}
    <div className="absolute -right-16 -top-6 hidden sm:block w-28 h-20 pointer-events-none">
      <Telescope className="absolute inset-0 w-full h-full" />
    </div>
    <Gear size={34} className="absolute -right-3 bottom-1 hidden sm:block" reverse speed={16} />
    <Gear size={24} className="absolute -right-1 -bottom-3 hidden sm:block" speed={12} />

    {/* Brass framed wooden mount */}
    <div className="sb-clockframe relative rounded-2xl px-6 pt-7 pb-4 sm:px-9 border-4 border-amber-950/80">
      {/* Arched engraved label */}
      <svg viewBox="0 0 240 36" className="absolute -top-4 left-1/2 -translate-x-1/2 w-56 h-9 pointer-events-none" aria-hidden="true">
        <defs>
          <linearGradient id="arch-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffe9b3" />
            <stop offset="55%" stopColor="#d99c2e" />
            <stop offset="100%" stopColor="#5a3a0c" />
          </linearGradient>
          <path id="arch-path" d="M14 26 Q120 -4 226 26" />
        </defs>
        <path d="M6 28 Q120 -8 234 28 L234 36 L6 36 Z" fill="url(#arch-grad)" stroke="#2a1808" strokeWidth="1.5" />
        <text fontSize="11" fontWeight="900" fill="#3a2407" letterSpacing="2" fontFamily="Georgia, serif">
          <textPath href="#arch-path" startOffset="50%" textAnchor="middle">{(typeof headerLabel === 'string' ? headerLabel : 'Next Draw In').toUpperCase()}</textPath>
        </text>
      </svg>

      {/* Brass corner brackets + rivets */}
      <span className="sb-bracket sb-bracket-tl" />
      <span className="sb-bracket sb-bracket-tr" />
      <span className="sb-bracket sb-bracket-bl" />
      <span className="sb-bracket sb-bracket-br" />

      <div className="flex gap-2 sm:gap-3 justify-center">
        <Dial value={timeLeft?.days} label={labels.days || 'Days'} />
        <Dial value={timeLeft?.hours} label={labels.hours || 'Hr'} />
        <Dial value={timeLeft?.minutes} label={labels.minutes || 'Min'} />
        <Dial value={timeLeft?.seconds} label={labels.seconds || 'Sec'} />
      </div>
    </div>

    {cardsRemaining != null && (
      <div className="text-center mt-2 text-amber-100/90 text-[11px] sm:text-xs font-semibold">
        <span className="font-black">{cardsRemaining}</span>{' '}
        {cardsRemainingLabel
          ? cardsRemainingLabel.replace('{{count}}', cardsRemaining)
          : `${cardsRemaining} cards remaining`}{' '}·{' '}
        {cardsOddsLabel
          ? cardsOddsLabel.replace('{{count}}', cardsRemaining)
          : `1 in ${cardsRemaining} odds`}
      </div>
    )}
  </div>
);

/* ────────────────────────────────────────────────────────────
 * 11. Stone parapet footer — full-width with arched alcoves
 * ──────────────────────────────────────────────────────────── */

const StoneAlcove = ({ children }) => (
  <div className="relative flex items-center gap-3">
    <div className="sb-alcove relative w-14 h-16 flex items-end justify-center pb-1.5 flex-shrink-0">
      <Candle />
    </div>
    <div className="text-left">{children}</div>
  </div>
);

const StoneParapet = ({
  jackpotLabel,
  jackpotAmount,
  weeklyLabel,
  weeklyAmount,
  buttonLabel,
  onButtonClick,
  potBuildingText = 'Pot Building',
  purchasesBlocked = false,
}) => (
  <div className="sb-stone sb-stone-blocks relative px-4 py-3 sm:py-4 border-t-4 border-black/60">
    {/* Top mortar shadow line */}
    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
    <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 items-center">
      <StoneAlcove>
        <div className="text-amber-200 text-[10px] sm:text-xs font-black uppercase tracking-widest">{jackpotLabel}</div>
        <div className="sb-gold-text text-2xl sm:text-3xl font-black tabular-nums leading-none" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          {jackpotAmount > 0 ? `$${Number(jackpotAmount).toLocaleString()}` : potBuildingText}
        </div>
      </StoneAlcove>
      <StoneAlcove>
        <div className="text-amber-200 text-[10px] sm:text-xs font-black uppercase tracking-widest">{weeklyLabel}</div>
        <div className="sb-gold-text text-xl sm:text-2xl font-black tabular-nums leading-none" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          {weeklyAmount > 0 ? `$${Number(weeklyAmount).toLocaleString()}` : potBuildingText}
        </div>
      </StoneAlcove>
      <div className="flex sm:justify-end justify-center">
        <TicketButton onClick={onButtonClick} size="sm" disabled={purchasesBlocked}>{buttonLabel}</TicketButton>
      </div>
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────
 * 12. Main StorybookHero
 * ──────────────────────────────────────────────────────────── */

const SAMPLE_PARTNERS = [
  { id: 'p1', name: 'YMCA',            logo: 'Y' },
  { id: 'p2', name: 'SPCA',            logo: '🐾' },
  { id: 'p3', name: 'Greener Village', logo: '🌳' },
  { id: 'p4', name: 'Food Bank',       logo: '🌽' },
];

export default function StorybookHero({
  weekNumber,
  liveLabel = 'LIVE NOW',
  buyTicketsLabel = 'Get Your Tickets',
  findAceLabel = 'Find the jackpot card to win!',
  previousDrawsLabel = 'Previous Draws',
  drawInLabel = 'Next Draw In',
  progressiveJackpotLabel = 'Progressive Jackpot',
  weeklyPrizeLabel = 'Weekly Prize',
  getTicketsLabel = 'Get Tickets',
  partnersHeader = 'Partners',
  charityLogoUrl,
  charityName,
  siteLogoUrl,
  siteName,
  jackpotAmount = 0,
  weeklyPrize = 0,
  cardsRemaining,
  eliminatedCards = [],
  timeLeft,
  partners = SAMPLE_PARTNERS,
  onBuyTickets,
  onPartnerSelect,
  purchasesBlocked = false,
  lifecycleState = null,
  targetCard,
  showFloatingCards = true,
  logoBackgroundColor = '#ffffff',
  descriptionSlot,
  buyTicketsSlot,
  findAceSlot,
  previousDrawsSlot,
  drawInSlot,
  progressiveJackpotSlot,
  weeklyPrizeSlot,
  getTicketsSlot,
  partnersHeaderSlot,
  liveSlot,
  partnerSelectSlot,
}) {
  const { t } = useTranslations();
  const countdownLabels = {
    days: t('countdown.days') || 'Days',
    hours: t('countdown.hours') || 'Hr',
    minutes: t('countdown.minutes') || 'Min',
    seconds: t('countdown.seconds') || 'Sec',
  };
  const weekLabel = t('chaseTheAce.week') || 'Week';
  const inPlayLabel = t('chaseTheAce.inPlay') || 'In Play';
  const cardsOutLabel = t('chaseTheAce.cardsOut') || 'Out';
  const cardsRemainingLabel = t('chaseTheAce.cardsRemaining') || '';
  const cardsOddsLabel = t('chaseTheAce.cardsOdds') || '';
  const [selectedPartnerId, setSelectedPartnerId] = useState(partners?.[0]?.id ?? null);

  const handleSelect = (p) => {
    setSelectedPartnerId(p.id);
    if (onPartnerSelect) onPartnerSelect(p);
  };

  return (
    <div className="relative">
      <section className="relative overflow-hidden text-white pt-8 pb-10 lg:pt-12 lg:pb-16 hero-fullscreen-mobile">
        <StarfieldBackdrop targetCard={targetCard} showCards={showFloatingCards} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
            {/* ───── LEFT COLUMN ───── */}
            <div className="flex flex-col items-center lg:items-start gap-5 sm:gap-6">
              <OrnatePlaque
                weekNumber={weekNumber}
                weekLabel={weekLabel}
                liveLabel={liveSlot || liveLabel}
                charityLogoUrl={charityLogoUrl}
                charityName={charityName}
                siteLogoUrl={siteLogoUrl}
                siteName={siteName}
                logoBackgroundColor={logoBackgroundColor}
              />

              <div className="mt-2 sm:mt-4 lg:self-stretch flex flex-col items-center gap-4 sm:gap-5">
                <StorybookTitle />
                <ScrollBanner>
                  {descriptionSlot}
                </ScrollBanner>
                {partnerSelectSlot}
                <TicketButton onClick={onBuyTickets} disabled={purchasesBlocked}>
                  {buyTicketsSlot || buyTicketsLabel}
                </TicketButton>
                <PreviousDraws
                  label={previousDrawsSlot || previousDrawsLabel}
                  eliminatedCards={eliminatedCards}
                />
              </div>
            </div>

            {/* ───── RIGHT COLUMN ───── */}
            <div className="flex flex-col items-center gap-6 sm:gap-7">
              <CrystalBall
                amount={jackpotAmount}
                ribbonText={findAceSlot || findAceLabel}
              />
              <PartnerPlaqueStack
                partners={partners}
                selectedId={selectedPartnerId}
                onSelect={handleSelect}
                headerLabel={partnersHeaderSlot || partnersHeader}
              />
              <div className="mt-2 self-center lg:self-end">
                {lifecycleState ? (
                  <DrawLifecycleMessage state={lifecycleState} compact className="max-w-xs" />
                ) : (
                  <ClockworkCountdown
                    timeLeft={timeLeft}
                    headerLabel={drawInSlot || drawInLabel}
                    cardsRemaining={cardsRemaining}
                    labels={countdownLabels}
                    cardsRemainingLabel={cardsRemainingLabel}
                    cardsOddsLabel={cardsOddsLabel}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <StoneParapet
        jackpotLabel={progressiveJackpotSlot || progressiveJackpotLabel}
        jackpotAmount={jackpotAmount}
        weeklyLabel={weeklyPrizeSlot || weeklyPrizeLabel}
        weeklyAmount={weeklyPrize}
        buttonLabel={getTicketsSlot || getTicketsLabel}
        onButtonClick={onBuyTickets}
        purchasesBlocked={purchasesBlocked}
      />
    </div>
  );
}
